import React from 'react';
import {FormCustomField, FormFieldValidationRules, FormProps, FormState} from '@src/@types/components/Form';
import app from '@src/helpers/app';
import {IFormFieldDesignContext} from '@src/@interfaces/contexts/IFormFieldDesignContext';
import {getFieldComponentByType} from './getFieldComponentByType';
import YupSchemeBuilder from '@src/services/YupSchemeBuilder/YupSchemeBuilder';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';

const emptyObject: Record<any, never> = {};

export default class FormAccessors<
    Model extends IEmptyModel = IEmptyModel,
    Props extends FormProps<Model> = FormProps<Model>,
    State extends FormState = FormState
> extends React.Component<Props, State> {
    protected get service() {
        return app('forms');
    }

    protected get config(): NonNullable<Props['config']> {
        return this.props.config as NonNullable<Props['config']>;
    }

    protected get controls() {
        return this.props.controls;
    }

    protected get fields() {
        return Object.assign(
            {},
            this.config.fields ?? emptyObject,
            this.config.tabs?.fields ?? emptyObject,
        );
    }

    protected get customFields(): Record<string, FormCustomField<Model>> {
        return this.props.customFields ?? emptyObject;
    }

    protected get customTabsContentWrappers(): Required<FormProps<Model>>['customTabsContentWrappers'] {
        return this.props.customTabsContentWrappers ?? emptyObject;
    }

    protected get customTabsControlWrappers(): Required<FormProps<Model>>['customTabsControlWrappers'] {
        return this.props.customTabsControlWrappers ?? emptyObject;
    }

    protected get formFieldDesignContextOptions(): Partial<IFormFieldDesignContext> {
        return emptyObject;
    }

    protected get customInitialValues(): Record<string, any> {
        return this.props.customInitialValues ?? emptyObject;
    }

    protected get initialValues() {
        const model = this.props.model || (emptyObject as Model);
        const fields = this.fields;

        const customInitialValues = this.customInitialValues;

        return Object.keys(fields).reduce((res: Record<string, any>, fieldName) => {
            const field = fields[fieldName];
            const isFileField = field.type === 'file_upload';

            const shouldCastToBoolean = (
                YupSchemeBuilder.FIELD_TYPES_BOOLEAN.includes(field.type as any) &&
                (
                    Array.isArray(field.validationRules) &&
                    field.validationRules?.includes(FormFieldValidationRules.REQUIRED) ||
                    field.validationRules &&
                    FormFieldValidationRules.REQUIRED in field.validationRules
                )
            );

            let defaultValue;
            if (fieldName in customInitialValues) {
                defaultValue = customInitialValues[fieldName];
            } else {
                defaultValue = 'defaultValue' in fields[fieldName] ?
                    field.defaultValue :
                    (isFileField ? null : '');
            }

            if (field.type === 'color_picker' && defaultValue === '') {
                defaultValue = '#000000';
            }

            if (shouldCastToBoolean) {
                defaultValue = false;
            }

            const was = isFileField ? null : (model[fieldName as keyof Model] ?? null);
            const became = this.mutateModelFieldInitialValue(fieldName, was);

            const isMutated = was !== became;
            if (isMutated) {
                res[fieldName] = became;

                return res;
            }

            res[fieldName] = became ?? fields[fieldName].default ?? defaultValue;
            if (shouldCastToBoolean && [null, undefined, ''].includes(res[fieldName])) {
                res[fieldName] = Boolean(res[fieldName]);
            }

            const value = res[fieldName];

            // extract relation/select field value
            const valueFrom = 'id';
            if (field.type !== 'custom' && value != null && typeof value === 'object') {
                if (
                    Array.isArray(value) &&
                    value.at(0) != null &&
                    typeof value.at(0) === 'object' &&
                    valueFrom in value.at(0)
                ) {
                    res[fieldName] = value.map(i => i[valueFrom]);
                } else if (valueFrom in value) {
                    res[fieldName] = value[valueFrom];
                }
            }

            return res;
        }, {});
    }

    protected get validationScheme() {
        return app('scheme-builder').getSchemeByFormConfig(this.config);
    }

    protected mutateModelFieldInitialValue(fieldName: string, modelValue: any | null): any {
        const config = this.getFieldConfigByName(fieldName);
        const component = getFieldComponentByType(config?.type ?? '');
        if (component && 'mutateValueBeforeDisplay' in component) {
            return (component as any).mutateValueBeforeDisplay(modelValue);
        }

        return modelValue;
    }

    protected getFieldConfigByName(fieldName: string) {
        const fields = this.fields;

        return fieldName in fields ? fields[fieldName] : null;
    }
}
